import React from "react";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

import type { ContentProgressItem } from "../../@types/portalAluno";
import MediaLoadingIndicator from "../MediaLoadingIndicator/MediaLoadingIndicator";
import "./styles.css";

interface EmbeddedContentViewerProps {
  item: ContentProgressItem;
  onExternalOpen?: () => boolean | void | Promise<boolean | void>;
  onScormComplete?: () => boolean | void | Promise<boolean | void>;
}

interface ScormWindow extends Window {
  API?: Record<string, (...args: string[]) => string>;
  API_1484_11?: Record<string, (...args: string[]) => string>;
}

const downloadableTypes: ContentProgressItem["contentType"][] = [
  "document",
  "download",
  "package",
  "presentation",
  "spreadsheet",
];

const audioFilePattern = /\.(?:aac|flac|m4a|mp3|oga|ogg|wav)(?:[?#].*)?$/i;
const videoFilePattern = /\.(?:3gp|3gpp|avi|m3u8|m4v|mkv|mov|mp4|webm)(?:[?#].*)?$/i;
const toolbarlessWebTypes: ContentProgressItem["contentType"][] = [
  "html",
  "internal",
  "scorm",
];

const completedScormStatuses = new Set(["complete", "completed", "passed"]);

const isScormCompletionValue = (key: string, value: string) => {
  const normalizedKey = key.toLocaleLowerCase("en-US");
  const normalizedValue = value.trim().toLocaleLowerCase("en-US");

  if (
    normalizedKey.endsWith("lesson_status") ||
    normalizedKey.endsWith("completion_status") ||
    normalizedKey.endsWith("success_status") ||
    normalizedKey === "status"
  ) {
    return completedScormStatuses.has(normalizedValue);
  }

  return (
    normalizedKey.endsWith("progress_measure") &&
    Number(normalizedValue) >= 1
  );
};

const getScormCompletionFromMessage = (
  value: unknown
): { key: string; value: string } | undefined => {
  if (typeof value === "string") {
    try {
      return getScormCompletionFromMessage(JSON.parse(value));
    } catch {
      const normalized = value.trim().toLocaleLowerCase("en-US");
      if (/^scorm[_\s:-]*(?:complete|completed|passed)$/.test(normalized)) {
        return { key: "completion_status", value: "completed" };
      }
      return undefined;
    }
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const data = value as Record<string, unknown>;
  const candidateKeys = [
    "cmi.core.lesson_status",
    "cmi.completion_status",
    "cmi.success_status",
    "cmi.progress_measure",
    "lesson_status",
    "completion_status",
    "success_status",
    "progress_measure",
    "status",
  ];

  for (const key of candidateKeys) {
    const candidate = data[key];
    if (
      (typeof candidate === "string" || typeof candidate === "number") &&
      isScormCompletionValue(key, String(candidate))
    ) {
      return { key, value: String(candidate) };
    }
  }

  for (const key of ["type", "event", "action"]) {
    const completion = getScormCompletionFromMessage(data[key]);
    if (completion) return completion;
  }

  if (data.data !== value) return getScormCompletionFromMessage(data.data);
  return undefined;
};

const EmbeddedContentViewer = ({
  item,
  onExternalOpen,
  onScormComplete,
}: EmbeddedContentViewerProps) => {
  const [isLoading, setIsLoading] = React.useState(Boolean(item.sourceUrl));
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const loadingItemKeyRef = React.useRef(`${item.id}:${item.sourceUrl ?? ""}`);
  const hasReportedScormCompletionRef = React.useRef(false);
  const isReportingScormCompletionRef = React.useRef(false);
  const onScormCompleteRef = React.useRef(onScormComplete);
  onScormCompleteRef.current = onScormComplete;
  const isAudioMedia =
    item.contentType === "audio" || audioFilePattern.test(item.sourceUrl ?? "");
  const isVideoMedia =
    !isAudioMedia &&
    (item.contentType === "video" || videoFilePattern.test(item.sourceUrl ?? ""));
  const isFullHeightWebContent = toolbarlessWebTypes.includes(item.contentType);

  React.useEffect(() => {
    const nextItemKey = `${item.id}:${item.sourceUrl ?? ""}`;
    if (loadingItemKeyRef.current !== nextItemKey) {
      loadingItemKeyRef.current = nextItemKey;
      setIsLoading(Boolean(item.sourceUrl));
    }
    hasReportedScormCompletionRef.current = false;
    isReportingScormCompletionRef.current = false;
  }, [item.id, item.sourceUrl]);

  React.useEffect(() => {
    if (item.contentType !== "scorm") return;

    const scormWindow = window as ScormWindow;
    const hadApi12 = Object.prototype.hasOwnProperty.call(scormWindow, "API");
    const hadApi2004 = Object.prototype.hasOwnProperty.call(
      scormWindow,
      "API_1484_11"
    );
    const previousApi12 = scormWindow.API;
    const previousApi2004 = scormWindow.API_1484_11;
    const values = new Map<string, string>();
    let lastError = "0";

    const reportCompletion = () => {
      if (
        hasReportedScormCompletionRef.current ||
        isReportingScormCompletionRef.current
      ) {
        return;
      }

      isReportingScormCompletionRef.current = true;
      Promise.resolve(onScormCompleteRef.current?.())
        .then((wasCompleted) => {
          if (wasCompleted !== false) {
            hasReportedScormCompletionRef.current = true;
          }
        })
        .finally(() => {
          isReportingScormCompletionRef.current = false;
        });
    };

    const setValue = (key: string, value: string) => {
      values.set(key, value);
      lastError = "0";
      if (isScormCompletionValue(key, value)) reportCompletion();
      return "true";
    };
    const getValue = (key: string) => values.get(key) ?? "";
    const initialize = () => {
      lastError = "0";
      return "true";
    };
    const finish = () => {
      lastError = "0";
      return "true";
    };
    const commit = () => "true";
    const getLastError = () => lastError;
    const getErrorString = () => "Sem erro";
    const getDiagnostic = () => "";

    scormWindow.API = {
      LMSCommit: commit,
      LMSFinish: finish,
      LMSGetDiagnostic: getDiagnostic,
      LMSGetErrorString: getErrorString,
      LMSGetLastError: getLastError,
      LMSGetValue: getValue,
      LMSInitialize: initialize,
      LMSSetValue: setValue,
    };
    scormWindow.API_1484_11 = {
      Commit: commit,
      GetDiagnostic: getDiagnostic,
      GetErrorString: getErrorString,
      GetLastError: getLastError,
      GetValue: getValue,
      Initialize: initialize,
      SetValue: setValue,
      Terminate: finish,
    };

    const handleScormMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (getScormCompletionFromMessage(event.data)) reportCompletion();
    };

    window.addEventListener("message", handleScormMessage);

    return () => {
      window.removeEventListener("message", handleScormMessage);
      if (hadApi12) scormWindow.API = previousApi12;
      else delete scormWindow.API;
      if (hadApi2004) scormWindow.API_1484_11 = previousApi2004;
      else delete scormWindow.API_1484_11;
    };
  }, [item.contentType, item.id]);

  if (item.externalUrl) {
    return (
      <section className="embedded-content-viewer embedded-content-viewer--message">
        <OpenInNewRoundedIcon aria-hidden="true" />
        <strong>{item.title}</strong>
        <p>Este conteúdo será aberto em uma nova aba.</p>
        <div className="embedded-content-viewer__actions">
          <a
            href={item.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => void onExternalOpen?.()}
          >
            Abrir conteúdo externo <OpenInNewRoundedIcon aria-hidden="true" />
          </a>
        </div>
      </section>
    );
  }

  if (!item.sourceUrl) {
    return (
      <section className="embedded-content-viewer embedded-content-viewer--message" role="alert">
        <DescriptionOutlinedIcon aria-hidden="true" />
        <strong>Arquivo não disponível no momento.</strong>
      </section>
    );
  }

  if (item.contentType === "image") {
    return (
      <section
        className={`embedded-content-viewer embedded-content-viewer--image${
          isLoading ? " embedded-content-viewer--image-loading" : ""
        }`}
        aria-label={`Imagem do módulo ${item.title}`}
      >
        <div className="embedded-content-viewer__viewport">
          {isLoading && (
            <MediaLoadingIndicator
              className="embedded-content-viewer__loading"
              label="Carregando conteúdo"
            />
          )}
          <img
            src={item.sourceUrl}
            alt={item.title}
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
          />
        </div>
      </section>
    );
  }

  if (isAudioMedia || isVideoMedia) {
    const mediaLabel = isAudioMedia ? "Áudio" : "Vídeo";

    return (
      <section
        className={`embedded-content-viewer embedded-content-viewer--${
          isAudioMedia ? "audio" : "video"
        }`}
        aria-label={`${mediaLabel} do módulo ${item.title}`}
      >
        <div className="embedded-content-viewer__viewport">
          {isLoading && (
            <MediaLoadingIndicator
              className="embedded-content-viewer__loading"
              label="Carregando conteúdo"
            />
          )}
          {isAudioMedia ? (
            <audio
              src={item.sourceUrl}
              controls
              preload="metadata"
              onCanPlay={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
            >
              Seu navegador não oferece suporte à reprodução de áudio.
            </audio>
          ) : (
            <video
              src={item.sourceUrl}
              controls={!isLoading}
              preload="metadata"
              playsInline
              onCanPlay={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
            >
              Seu navegador não oferece suporte à reprodução de vídeo.
            </video>
          )}
        </div>
      </section>
    );
  }

  if (downloadableTypes.includes(item.contentType)) {
    return (
      <section className="embedded-content-viewer embedded-content-viewer--message">
        <DescriptionOutlinedIcon aria-hidden="true" />
        <strong>{item.title}</strong>
        <p>Este formato deve ser aberto ou baixado no seu dispositivo.</p>
        <div className="embedded-content-viewer__actions">
          <a href={item.sourceUrl} download>
            Baixar arquivo
          </a>
          <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
            Abrir arquivo <OpenInNewRoundedIcon aria-hidden="true" />
          </a>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`embedded-content-viewer${
        isFullHeightWebContent ? " embedded-content-viewer--web-full" : ""
      }`}
      aria-label={`Visualizador do módulo ${item.title}`}
    >
      {!isFullHeightWebContent && (
        <div className="embedded-content-viewer__toolbar">
          <strong>{item.title}</strong>
          <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
            Abrir em nova aba <OpenInNewRoundedIcon aria-hidden="true" />
          </a>
        </div>
      )}

      <div className="embedded-content-viewer__viewport">
        {isLoading && (
          <MediaLoadingIndicator
            className="embedded-content-viewer__loading"
            label="Carregando conteúdo"
          />
        )}
        <iframe
          ref={iframeRef}
          src={item.sourceUrl}
          title={item.title}
          allow="autoplay; clipboard-read; clipboard-write; fullscreen; picture-in-picture"
          allowFullScreen
          onLoad={() => setIsLoading(false)}
        />
      </div>
    </section>
  );
};

export default EmbeddedContentViewer;
